import * as yup from 'yup'

import type { OptionProps, CompanyForFindOne } from 'types'
import { CompanyForForm, FILE_REF_TYPE } from 'types'
import { CompanyService } from '.'

type Context = yup.TestContext & { type?: string }

export const initialCompanyDetail: CompanyForFindOne = {
  id: '',
  code: '',
  name: '',
  contacts: [],
  faxes: [],
  websites: [],
  logo: '',
  detail: '',
  addresses: [
    {
      id: '',
      refId: '',
      refType: FILE_REF_TYPE.COMPANY,
      type: '',
      address: '',
      countryEn: '',
      countryTh: '',
      provinceEn: '',
      provinceTh: '',
      districtEn: '',
      districtTh: '',
      subDistrictEn: '',
      subDistrictTh: '',
      postalCode: '',
    },
  ],
  taxId: '',
  isActive: false,
  industry: {
    id: '',
    code: '',
    nameEn: '',
    nameTh: '',
  },
  role: {
    id: '',
    nameEn: '',
    nameTh: '',
  },
  createBy: '',
  createdAt: '',
  updatedBy: '',
  updatedAt: '',
  deleteBy: '',
  deletedAt: '',
  logoPath: '',
}

export const schemaCompanyForm = yup.object().shape({
  code: yup.string(),
  name: yup
    .string()
    .max(100)
    .required('company:nameRequired.label')
    .test('name', async (name, context: Context) => {
      const { path, resolve, createError, parent, type } = context

      if (!name) {
        return resolve(true)
      }

      if (
        parent?.currentState === type ||
        parent?.currentState === 'onSubmit'
      ) {
        const _parent: CompanyForForm = parent

        const response = await CompanyService.findAll({
          name: name.trim(),
          _limit: '0',
        })
        const company = response.body.find(
          (_company) =>
            _company.name.toLowerCase() === name.trim().toLocaleLowerCase(),
        )

        const isSameOriginalName = parent?.originalName === name.trim()

        if (!company) {
          return resolve(true)
        }

        if (company.code === _parent.code || isSameOriginalName) {
          return resolve(true)
        }

        return createError({
          path,
          message: 'company:nameDuplicated.label',
        })
      }

      return resolve(true)
    }),
  taxId: yup
    .string()
    .max(20)
    .required('common:taxIdRequired.label')
    .test('taxId', async (taxId, context: Context) => {
      const { path, resolve, createError, parent, type } = context

      if (!taxId) {
        return resolve(true)
      }

      if (
        parent?.currentState === type ||
        parent?.currentState === 'onSubmit'
      ) {
        const _parent: CompanyForForm = parent
        const response = await CompanyService.findAll({
          taxId: taxId.trim(),
          _limit: '0',
        })
        const company = response.body.find(
          (_company) =>
            _company.taxId.toLowerCase() === taxId.trim().toLowerCase(),
        )
        const isSameOriginalTaxId = parent?.originalTaxId === taxId.trim()

        if (!company) {
          return resolve(true)
        }

        if (company.code === _parent.code || isSameOriginalTaxId) {
          return resolve(true)
        }

        return createError({
          path,
          message: 'common:taxIdDuplicated.label',
        })
      }

      return resolve(true)
    }),
  email: yup
    .string()
    .max(50)
    .required('common:emailRequired.label')
    .email('common:emailInvalid.label'),
  countryCode: yup.string(),
  phoneNumber: yup
    .string()
    .max(15)
    .test('phoneNumber', async (phoneNumber, context: Context) => {
      const { path, resolve, createError } = context

      const isPhoneOffice =
        phoneNumber?.startsWith('02') &&
        phoneNumber?.replace('-', '').length !== 9
      const isPhoneNormal =
        !phoneNumber?.startsWith('02') &&
        phoneNumber?.replace('-', '').length !== 10

      if (phoneNumber && (isPhoneOffice || isPhoneNormal)) {
        return createError({
          path,
          message: 'company:phoneInvalid.label',
        })
      }

      return resolve(true)
    }),
  address: yup.string().max(100).required('common:addressRequired.label'),
  country: yup.object().shape({
    id: yup.string().required('common:countryRequired.label'),
    countryTh: yup.string().required('common:countryRequired.label'),
    countryEn: yup.string().required('common:countryRequired.label'),
  }),
  province: yup.object().shape({
    id: yup.string().required('common:provinceRequired.label'),
    provinceTh: yup.string().required('common:provinceRequired.label'),
    provinceEn: yup.string().required('common:provinceRequired.label'),
  }),
  district: yup.object().shape({
    id: yup.string().required('common:districtRequired.label'),
    districtTh: yup.string().required('common:districtRequired.label'),
    districtEn: yup.string().required('common:districtRequired.label'),
  }),
  subDistrict: yup.object().shape({
    id: yup.string().required('common:subdistrictRequired.label'),
    subDistrictTh: yup.string().required('common:subdistrictRequired.label'),
    subDistrictEn: yup.string().required('common:subdistrictRequired.label'),
  }),
  postalCode: yup.string().max(8).required('common:postalCodeRequired.label'),
  detail: yup.string().max(1000),
  isActive: yup.boolean(),
})

export const initialCompanyForm: CompanyForForm = {
  currentState: 'name',
  originalName: '',
  originalTaxId: '',
  code: '',
  name: '',
  taxId: '',
  email: '',
  countryCode: '+66',
  phoneNumber: '',
  logo: '',
  address: '',
  country: {
    id: '0ce9c9b3-3ae9-49fe-9455-56e1e2595e42',
    countryEn: 'Thailand',
    countryTh: 'ประเทศไทย',
  },
  province: {
    id: '',
  },
  district: {
    id: '',
  },
  subDistrict: {
    id: '',
  },
  postalCode: '',
  detail: '',
  isActive: true,
}

export const findSelected = (
  options?: OptionProps[] | null,
  key?: string,
): OptionProps | null | undefined => {
  if (!options) {
    return null
  }

  return options.find((item: OptionProps) => item.key === key)
}
